import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { getAllTenantProvisioningRuns } from '../api/tenantProvisioningApi';
import { DataTable } from '../components/DataTable';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import { PageShell } from '../components/PageShell';
import { StatusBadge } from '../components/StatusBadge';

export function TenantProvisioningRunsPage() {
  const { t } = useTranslation(['tenantProvisioning', 'common']);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllTenantProvisioningRuns({ per_page: 100 });
      setRows(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => [
    { id: 'tenant_id', label: t('tenantProvisioning:runs.columns.tenant'), render: (row) => <RouterLink to={`/tenants/${row.tenant_id}`}>{t('tenantProvisioning:dashboard.tenant_link', { id: row.tenant_id })}</RouterLink> },
    { id: 'run_type', label: t('tenantProvisioning:runs.columns.run_type') },
    { id: 'status', label: t('common:status'), render: (row) => <StatusBadge status={row.status} category="provisioning_run" /> },
    { id: 'mode', label: t('tenantProvisioning:runs.columns.mode') },
    { id: 'database_name', label: t('tenantProvisioning:runs.columns.database') },
    { id: 'started_at', label: t('tenantProvisioning:runs.columns.started') },
    { id: 'completed_at', label: t('tenantProvisioning:runs.columns.completed') },
  ], [t]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={t('tenantProvisioning:runs.load_failed')} onRetry={load} />;

  return (
    <PageShell title={t('tenantProvisioning:runs.title')} subtitle={t('tenantProvisioning:runs.subtitle')}>
      <DataTable columns={columns} rows={rows} rowKey="id" emptyMessage={t('tenantProvisioning:runs.no_runs')} />
    </PageShell>
  );
}
